import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageProvider';
import { useThemeContext } from '../context/ThemeProvider';
import PresetPicker from './drumset/PresetPicker';
import InstrumentList from './drumset/InstrumentList';
import type { DrumInstrument, DrumsetConfig } from '../types';

interface DrumsetEditorProps {
  onSave?: (config: DrumsetConfig) => void;
  onBack?: () => void;
  currentConfig?: DrumsetConfig | null;
  showDynamicMessage?: (message: string, type?: "info" | "success" | "warning" | "error") => void;
}

export default function DrumsetEditor({ 
  onSave, 
  onBack, 
  currentConfig, 
  showDynamicMessage 
}: DrumsetEditorProps) {
  const { t } = useLanguage();
  const { getClass } = useThemeContext();

  const getThemeClass = (type: string): string => {
    try {
      return getClass(type) || '';
    } catch {
      const fallback: Record<string, string> = {
        background: 'bg-white',
        text: 'text-gray-900',
        button: 'bg-blue-600 hover:bg-blue-700 text-white',
        grid: 'bg-gray-100',
        border: 'border-gray-300',
        input: 'border-gray-300 bg-white text-gray-900',
        card: 'bg-white border-gray-200 shadow-sm',
      };
      return fallback[type] || '';
    }
  };

  const [instruments, setInstruments] = useState<DrumInstrument[]>([]);

  useEffect(() => {
    if (currentConfig && currentConfig.instruments) {
      setInstruments(currentConfig.instruments);
    } else {
      const saved = localStorage.getItem('drumsetConfig');
      if (saved) {
        try {
          const cfg = JSON.parse(saved);
          if (cfg.instruments) setInstruments(cfg.instruments);
        } catch {}
      }
    }
  }, [currentConfig]);

  const handleSave = (config: DrumsetConfig): void => {
    if (onSave) onSave(config);
  };

  return (
    <div className="space-y-6">
      {/* 顯示當前鼓組名稱 */}
      {currentConfig?.name && (
        <div className={`p-3 rounded ${getThemeClass('card')} ${getThemeClass('border')}`}>
          <div className="flex items-center space-x-2">
            <span className="text-lg">🎵</span>
            <span className={`font-medium ${getThemeClass('text')}`}>
              {t('customKit')}: {currentConfig.name}
            </span>
          </div>
        </div>
      )}
      
      <PresetPicker 
        onPresetLoaded={setInstruments} 
        t={t} 
        getThemeClass={getThemeClass}
        currentInstruments={instruments}
      />
      <InstrumentList
        instruments={instruments}
        setInstruments={setInstruments}
        t={t}
        getThemeClass={getThemeClass}
        showDynamicMessage={showDynamicMessage}
        onSave={handleSave}
      />
    </div>
  );
} 